import {
  useCallback,
  useEffect,
  useState,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useMutation } from "react-query";
import { Box, IconButton, ListItem } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import useAuthentication from "../../../hooks/useAuthentication";
import usePopup from "../../../hooks/usePopup";
import { addCptFavourite } from "../../services";
import { clinicId } from "../../../constants";

export type CptFavoriteOption = {
  cptId: number;
  cptCode?: string;
  cptName?: string;
  cptDes?: string;
  insCoverage?: number;
  isFavorite?: number;
};

type UseCptFavouriteOptions = {
  cptOptions?: CptFavoriteOption[];
};

export default function useCptFavourite({ cptOptions }: UseCptFavouriteOptions = {}) {
  const { showSnackbar } = usePopup();
  const { user } = useAuthentication();
  const activitySearchUserId =
    Number(user?.userId ?? user?.login_id) || null;

  const [favoriteCptIds, setFavoriteCptIds] = useState<Set<number>>(new Set());
  const [unfavoriteCptIds, setUnfavoriteCptIds] = useState<Set<number>>(new Set());
  const [cptListOpen, setCptListOpen] = useState(false);

  const isCptFavorite = useCallback(
    (option: CptFavoriteOption) => {
      if (unfavoriteCptIds.has(option.cptId)) return false;
      if (favoriteCptIds.has(option.cptId)) return true;
      return Number(option.isFavorite) === 1;
    },
    [favoriteCptIds, unfavoriteCptIds],
  );

  useEffect(() => {
    const favoriteIds =
      cptOptions
        ?.filter((item) => Number(item.isFavorite) === 1)
        .map((item) => item.cptId) ?? [];
    if (!favoriteIds.length) return;
    setFavoriteCptIds((prev) => {
      const next = new Set(prev);
      favoriteIds.forEach((id) => next.add(id));
      return next;
    });
    setUnfavoriteCptIds((prev) => {
      const next = new Set(prev);
      favoriteIds.forEach((id) => next.delete(id));
      return next;
    });
  }, [cptOptions]);

  const { mutate: saveCptFavourite, isLoading: savingCptFavourite } =
    useMutation(addCptFavourite, {
      onSuccess: (result, variables) => {
        if (result.responseIndicator === "success") {
          if (variables.isFavorite === 1) {
            setFavoriteCptIds((prev) => new Set(prev).add(variables.cptId));
            setUnfavoriteCptIds((prev) => {
              const next = new Set(prev);
              next.delete(variables.cptId);
              return next;
            });
          } else {
            setFavoriteCptIds((prev) => {
              const next = new Set(prev);
              next.delete(variables.cptId);
              return next;
            });
            setUnfavoriteCptIds((prev) => new Set(prev).add(variables.cptId));
          }
          setCptListOpen(true);
          showSnackbar({
            message:
              result.responseMessage ||
              (variables.isFavorite === 1
                ? "CPT added to favourites."
                : "CPT removed from favourites."),
            open: true,
            variant: "success",
            duration: 2000,
          });
        } else {
          showSnackbar({
            message: result.responseMessage || "Could not update favourite CPT.",
            open: true,
            variant: "warning",
            duration: 3000,
          });
        }
      },
      onError: (error: { response?: { data?: { responseMessage?: string } } }) => {
        showSnackbar({
          message:
            error?.response?.data?.responseMessage ||
            "Could not update favourite CPT.",
          open: true,
          variant: "error",
          duration: 3000,
        });
      },
    });

  const handleToggleCptFavourite = (
    option: CptFavoriteOption,
    event: MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    event.preventDefault();
    setCptListOpen(true);
    if (!activitySearchUserId) {
      showSnackbar({
        message: "User id is required to save favourite CPT.",
        open: true,
        variant: "warning",
        duration: 3000,
      });
      return;
    }
    if (savingCptFavourite) return;

    const isFavorite = isCptFavorite(option) ? 0 : 1;
    saveCptFavourite({
      cptId: option.cptId,
      userId: activitySearchUserId,
      clinicId,
      isFavorite,
    });
  };

  const renderCptFavoriteAction = (option: CptFavoriteOption) => (
    <IconButton
      size="small"
      aria-label={
        isCptFavorite(option)
          ? "Remove CPT from favourites"
          : "Add CPT to favourites"
      }
      disabled={savingCptFavourite}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onClick={(e) => handleToggleCptFavourite(option, e)}
      sx={{ flexShrink: 0, p: 0.5 }}
    >
      {isCptFavorite(option) ? (
        <StarIcon color="warning" sx={{ fontSize: "1.25rem" }} />
      ) : (
        <StarBorderIcon sx={{ fontSize: "1.25rem" }} />
      )}
    </IconButton>
  );

  const renderCptOptionListItem = (
    props: HTMLAttributes<HTMLLIElement> & { key?: string | number },
    option: CptFavoriteOption,
    children: ReactNode,
  ) => {
    const { key, ...optionProps } = props;
    return (
      <ListItem
        key={key}
        {...optionProps}
        disablePadding
        sx={{ py: 1, px: 1.5, alignItems: "flex-start" }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 0.5,
            width: "100%",
            minWidth: 0,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
            {children}
          </Box>
          <Box sx={{ flexShrink: 0, alignSelf: "center" }}>
            {renderCptFavoriteAction(option)}
          </Box>
        </Box>
      </ListItem>
    );
  };

  return {
    cptListOpen,
    setCptListOpen,
    isCptFavorite,
    savingCptFavourite,
    handleToggleCptFavourite,
    renderCptFavoriteAction,
    renderCptOptionListItem,
    activitySearchUserId,
  };
}
